import { useEffect, useState } from "react";

/**
 * A router for exactly two destinations.
 *
 * No routing library: this needs pushState, popstate and a match — about thirty
 * lines. A router package would add a dependency, a bundle cost and an API
 * surface to the critical path of a screen whose entire promise is opening fast.
 * If this ever grows past a handful of routes, revisit that trade.
 *
 * Real URLs, so the back button works and either screen can be linked or
 * bookmarked. The Worker serves index.html for any unmatched path
 * (`not_found_handling: "single-page-application"`), so a cold load of /themes
 * works too.
 */

export const ROUTES = {
  convert: "/",
  themes: "/themes",
} as const;

export type RouteName = keyof typeof ROUTES;

export function routeFromPath(pathname: string): RouteName {
  return pathname.replace(/\/+$/, "") === "/themes" ? "themes" : "convert";
}

export function useRoute(): [RouteName, (next: RouteName) => void] {
  const [route, setRoute] = useState<RouteName>(() =>
    routeFromPath(typeof location === "undefined" ? "/" : location.pathname),
  );

  useEffect(() => {
    const onPop = () => setRoute(routeFromPath(location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = (next: RouteName) => {
    if (next === route) return;
    history.pushState({ route: next }, "", ROUTES[next]);
    setRoute(next);
    // A route change is a new screen; start it at the top.
    window.scrollTo(0, 0);
  };

  return [route, navigate];
}
